import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
} from '@mui/material';

const PrivacyPolicy = ({ open, onClose }) => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Privacy Policy
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Effective Date: {currentDate}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            Vendoroll values your privacy and is committed to protecting your personal information in compliance with Alberta's Personal Information Protection Act (PIPA) and the federal Personal Information Protection and Electronic Documents Act (PIPEDA).
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>1. Information We Collect</strong>{'\n'}
            • <strong>Personal Identification:</strong> name, email address, phone number, business name, business type, and other registration information.{'\n'}
            • <strong>Profile Data:</strong> vendor profiles, event applications, and other content uploaded to the Platform.{'\n'}
            • <strong>Technical Data:</strong> IP address, browser type, operating system, login times, pages visited, click data, and device information.{'\n'}
            • <strong>Communications:</strong> emails, feedback, and inquiries sent to Vendoroll.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>2. How We Use Your Information</strong>{'\n'}
            • To operate, maintain, and improve the Platform.{'\n'}
            • To connect vendors with markets and organizers.{'\n'}
            • To communicate updates, service announcements, and relevant offers (including partner perks).{'\n'}
            • To analyze usage patterns, trends, and engagement metrics for internal business purposes.{'\n'}
            • To enforce our Terms of Service and comply with legal obligations.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>3. Legal Basis for Processing</strong>{'\n'}
            • <strong>Consent:</strong> You agree to the collection and use of your information by accepting this Privacy Policy.{'\n'}
            • <strong>Contractual Necessity:</strong> To provide you with the services requested.{'\n'}
            • <strong>Legal Obligations:</strong> To comply with applicable laws and regulations.{'\n'}
            • <strong>Legitimate Interests:</strong> For internal analysis, marketing, and platform improvement.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>4. Sharing and Disclosure of Information</strong>{'\n'}
            • <strong>Service Providers:</strong> Third-party service providers necessary to operate the Platform (e.g., hosting, analytics, email service).{'\n'}
            • <strong>Legal Requirements:</strong> When required by law or legal process.{'\n'}
            • <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, user information may be transferred.{'\n'}
            • <strong>No Sale of Data:</strong> Vendoroll does not sell your personal information to third parties.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>5. Cookies and Tracking Technologies</strong>{'\n'}
            Vendoroll uses cookies, pixels, and analytics tools to improve user experience, measure engagement, and deliver targeted content. Users may manage or disable cookies through browser settings; some features may not function fully if disabled.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>6. Data Security</strong>{'\n'}
            Vendoroll implements administrative, technical, and physical security measures to protect personal information against unauthorized access, loss, or misuse. While we strive to protect your data, no method of electronic transmission is 100% secure, and we cannot guarantee absolute security.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>7. Data Retention</strong>{'\n'}
            Vendoroll retains personal information only as long as necessary for the purposes outlined in this Privacy Policy, or as required by law. Once information is no longer required, it will be securely deleted or anonymized.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>8. International Data Transfers</strong>{'\n'}
            Vendoroll may store or process data on servers located in Canada or other countries. We ensure appropriate safeguards for international data transfers in compliance with applicable privacy laws.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>9. Your Rights</strong>{'\n'}
            • Access, correction, or deletion of personal information.{'\n'}
            • Withdrawal of consent where processing is based on consent.{'\n'}
            • Objection to certain processing, including marketing communications.{'\n'}
            • Complaint submission to privacy authorities if you believe your rights are violated.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
            <strong>10. Contact Information</strong>{'\n'}
            For questions, concerns, or requests regarding your personal information, please contact:{' '}
            <Typography 
              component="a" 
              href="mailto:info@vendoroll.com" 
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              info@vendoroll.com
            </Typography>.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyPolicy;


